// Support Communauté — conversation Admin ↔ membre
// Source de vérité : historique D1 du Worker Communauté (via Worker Admin, permission COMMUNAUTE_SUPPORT).
//  - GET  /admin/communaute/support/conversations                  → liste des fils
//  - GET  /admin/communaute/support/conversations/:memberId/messages → historique d'un fil
//  - POST /admin/communaute/support/messages                        → répondre
//  - POST /admin/communaute/support/messages/ack                    → marquer « lu » à l'ouverture

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Headphones, RefreshCw, Send, ShieldAlert, User } from 'lucide-react';
import * as communauteApi from '../services/adminCommunaute';
import { PageHeader, EmptyState } from '../components/ui/UIComponents';

/* ---- helpers purs ---- */

/** Enveloppe { success, data: {...} } → data ; tolère une réponse déjà « dépliée ». */
function unwrap(res) {
  if (!res) return {};
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  return res;
}

function itemsOf(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.items)) return d.items;
  return [];
}

function messageIdOf(m) {
  return m?.id || m?.messageId || m?.message_id || null;
}

function bodyOf(m) {
  return m?.body || m?.text || m?.content || '';
}

function isFromAdmin(m) {
  if (m?.official === true || m?.isAdmin === true || m?.fromAdmin === true) return true;
  const from = String(m?.from || m?.direction || m?.sender || '').toLowerCase();
  return ['admin', 'support', 'outbound', 'support_to_member'].includes(from);
}

/** Message du membre que l'Admin n'a pas encore « lu » (non accusé côté Worker). */
function isUnackedMemberMessage(m) {
  return !isFromAdmin(m) && m?.deliveredToAdminAt == null && Boolean(messageIdOf(m));
}

const DAY_MS = 86400000;

/** Horodatage d'un message en millisecondes, ou null s'il est absent/illisible. */
function tsOf(m) {
  const v = m?.createdAt ?? m?.created_at ?? null;
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v < 1e11 ? v * 1000 : v; // secondes → ms
  const asNumber = Number(v);
  if (!Number.isNaN(asNumber) && String(v).trim() !== '') {
    return asNumber < 1e11 ? asNumber * 1000 : asNumber;
  }
  const parsed = Date.parse(v);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Tri du plus ancien au plus récent. Sans date = tout en bas. Égalité = ordre d'arrivée. */
function sortByTime(list) {
  return list
    .map((m, i) => ({ m, i, t: tsOf(m) }))
    .sort((a, b) => {
      const ta = a.t == null ? Number.MAX_SAFE_INTEGER : a.t;
      const tb = b.t == null ? Number.MAX_SAFE_INTEGER : b.t;
      return ta - tb || a.i - b.i;
    })
    .map((x) => x.m);
}

/** Fusionne par id (le serveur fait foi), retire les messages provisoires « local- », trie. */
function mergeMessages(prev, incoming) {
  const map = new Map();
  const hasServerData = incoming.some((m) => messageIdOf(m) && !String(messageIdOf(m)).startsWith('local-'));
  prev.forEach((m) => {
    const id = messageIdOf(m);
    if (!id) return;
    if (hasServerData && String(id).startsWith('local-')) return;
    map.set(id, m);
  });
  incoming.forEach((m) => {
    const id = messageIdOf(m);
    if (id) map.set(id, { ...(map.get(id) || {}), ...m });
  });
  return sortByTime(Array.from(map.values()));
}

function normalizeConversation(c) {
  return {
    conversationId: c?.conversationId || null,
    memberId: c?.memberId || null,
    lastMessageAt: Number(c?.lastMessageAt) || 0,
    messageCount: Number(c?.messageCount) || 0,
    unreadCount: Number(c?.unreadCount) || 0,
    lastMessage: {
      body: c?.lastMessage?.body || '',
      direction: c?.lastMessage?.direction || 'member_to_support',
      createdAt: Number(c?.lastMessage?.createdAt) || Number(c?.lastMessageAt) || 0,
    },
  };
}

function sortConversations(list) {
  return [...list].sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
}

/** Met à jour (ou crée) l'entrée de liste d'un membre après l'envoi d'une réponse. */
function bumpConversation(list, memberId, msg) {
  const at = tsOf(msg) ?? Date.now();
  const last = { body: bodyOf(msg), direction: 'support_to_member', createdAt: at };
  const exists = list.some((c) => c.memberId === memberId);
  const next = exists
    ? list.map((c) =>
        c.memberId === memberId
          ? { ...c, lastMessageAt: at, messageCount: c.messageCount + 1, lastMessage: last }
          : c
      )
    : [
        ...list,
        normalizeConversation({ memberId, lastMessageAt: at, messageCount: 1, unreadCount: 0, lastMessage: last }),
      ];
  return sortConversations(next);
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function dayKey(t) {
  const d = new Date(t);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(t) {
  const d = new Date(t);
  const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / DAY_MS);
  if (diff === 0) return 'Aujourd’hui';
  if (diff === 1) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(t) {
  if (t == null) return '';
  return new Date(t).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Petit horodatage pour la liste : heure si aujourd'hui, sinon jj/mm. */
function formatListWhen(t) {
  if (!t) return '';
  const diff = Math.round((startOfDay(new Date()) - startOfDay(new Date(t))) / DAY_MS);
  if (diff === 0) return formatTime(t);
  return new Date(t).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/* ---- Page ---- */

const POLL_MS = 15000;
const PAGE_SIZE = 50;

export default function CommunitySupport() {
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const selectedRef = useRef(null); // membre ouvert (pour ignorer les réponses périmées)
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const restoreRef = useRef(null); // hauteur avant chargement des anciens messages
  const lastIdRef = useRef(null); // dernier message affiché (pour ne défiler que sur un nouveau)
  const tickRef = useRef(null);

  const humanErr = (err, fallback) => {
    if (err?.status === 403) return 'Vous n’avez pas l’autorisation d’accéder au support.';
    if (err?.status === 401) return 'Session expirée. Veuillez vous reconnecter.';
    if (err?.status === 404) return 'Membre introuvable dans la communauté.';
    if (err?.status === 502 || err?.status === 503) return 'Le service support est temporairement indisponible.';
    return err?.message || fallback;
  };

  /* ---------- Liste des fils ---------- */
  const loadConversations = async ({ silent = false } = {}) => {
    try {
      const res = await communauteApi.getSupportConversations({ limit: 100 });
      const list = itemsOf(unwrap(res)).map(normalizeConversation).filter((c) => c.memberId);
      setConversations(sortConversations(list));
      setListError(null);
    } catch (err) {
      if (!silent) setListError(humanErr(err, 'Impossible de charger les conversations.'));
    } finally {
      setListLoading(false);
    }
  };

  /* ---------- « Lu » : accuser les messages du membre à l'ouverture du fil ---------- */
  const ackMemberMessages = async (memberId, items) => {
    const ids = items.filter(isUnackedMemberMessage).map(messageIdOf);
    if (!ids.length) return;
    try {
      await communauteApi.ackSupportMessages({ messageIds: ids });
      const acked = new Set(ids);
      const now = Date.now();
      setThread((prev) => prev.map((m) => (acked.has(messageIdOf(m)) ? { ...m, deliveredToAdminAt: now } : m)));
      setConversations((prev) =>
        prev.map((c) =>
          c.memberId === memberId ? { ...c, unreadCount: Math.max(0, c.unreadCount - ids.length) } : c
        )
      );
    } catch {
      // Non bloquant : le compteur « non lu » reste affiché et sera retenté à la prochaine ouverture.
    }
  };

  /* ---------- Fil d'un membre ---------- */
  const loadThread = async (memberId) => {
    setThreadLoading(true);
    setThreadError(null);
    setThread([]);
    setHasMore(false);
    setNextBefore(null);
    try {
      const res = await communauteApi.getSupportConversationMessages(memberId, { limit: PAGE_SIZE });
      if (selectedRef.current !== memberId) return;
      const d = unwrap(res);
      const items = itemsOf(d);
      setThread(sortByTime(items));
      setHasMore(Boolean(d.hasMore));
      setNextBefore(d.nextBefore ?? null);
      ackMemberMessages(memberId, items);
    } catch (err) {
      if (selectedRef.current === memberId) setThreadError(humanErr(err, 'Impossible de charger la conversation.'));
    } finally {
      if (selectedRef.current === memberId) setThreadLoading(false);
    }
  };

  // Rafraîchissement discret (polling) : récupère les derniers messages et les fusionne.
  const refreshThread = async (memberId) => {
    try {
      const res = await communauteApi.getSupportConversationMessages(memberId, { limit: PAGE_SIZE });
      if (selectedRef.current !== memberId) return;
      const items = itemsOf(unwrap(res));
      setThread((prev) => mergeMessages(prev, items));
      ackMemberMessages(memberId, items);
    } catch {
      // Silencieux : la prochaine passe réessaiera.
    }
  };

  const loadOlder = async () => {
    const memberId = selectedRef.current;
    if (!memberId || !hasMore || loadingOlder || nextBefore == null) return;
    setLoadingOlder(true);
    restoreRef.current = scrollRef.current ? scrollRef.current.scrollHeight : null;
    try {
      const res = await communauteApi.getSupportConversationMessages(memberId, {
        limit: PAGE_SIZE,
        before: nextBefore,
      });
      if (selectedRef.current !== memberId) return;
      const d = unwrap(res);
      setThread((prev) => mergeMessages(prev, itemsOf(d)));
      setHasMore(Boolean(d.hasMore));
      setNextBefore(d.nextBefore ?? null);
    } catch (err) {
      restoreRef.current = null;
      setThreadError(humanErr(err, 'Impossible de charger les messages précédents.'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const selectConversation = (memberId) => {
    selectedRef.current = memberId;
    lastIdRef.current = null;
    setSelectedMemberId(memberId);
    setSendError(null);
    setDraft('');
    loadThread(memberId);
  };

  const closeConversation = () => {
    selectedRef.current = null;
    lastIdRef.current = null;
    setSelectedMemberId(null);
    setThread([]);
    setThreadError(null);
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadConversations();
    if (selectedRef.current) await refreshThread(selectedRef.current);
    setRefreshing(false);
  };

  /* ---------- Envoi d'une réponse ---------- */
  const handleSend = async () => {
    const text = draft.trim();
    const memberId = selectedRef.current;
    if (!text || !memberId || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await communauteApi.sendSupportMessage({ memberId, body: text });
      // Réponse du Worker : { data: { conversationId, message: {...}, duplicate } }
      const msg = unwrap(res).message || null;
      const now = Date.now();
      const created = {
        ...(msg || {}),
        id: messageIdOf(msg) || `local-${now}`,
        memberId,
        body: bodyOf(msg) || text,
        direction: 'support_to_member',
        official: true,
        // Toujours une date valide : c'est elle qui place le message au bon endroit
        createdAt: tsOf(msg) ?? now,
      };
      if (selectedRef.current === memberId) setThread((prev) => mergeMessages(prev, [created]));
      setConversations((prev) => bumpConversation(prev, memberId, created));
      setDraft('');
      if (!msg) refreshThread(memberId); // le serveur remplacera le message provisoire
    } catch (err) {
      setSendError(humanErr(err, 'L’envoi a échoué. Réessayez.'));
    } finally {
      setSending(false);
    }
  };

  /* ---------- Polling (onglet visible uniquement) ---------- */
  tickRef.current = async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    await loadConversations({ silent: true });
    if (selectedRef.current) await refreshThread(selectedRef.current);
  };

  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => tickRef.current && tickRef.current(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && tickRef.current) tickRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  /* ---------- Défilement ---------- */
  // Après « charger les messages précédents » : garder la position de lecture.
  useLayoutEffect(() => {
    if (restoreRef.current != null && scrollRef.current) {
      scrollRef.current.scrollTop += scrollRef.current.scrollHeight - restoreRef.current;
      restoreRef.current = null;
    }
  }, [thread]);

  // Aller en bas uniquement quand un NOUVEAU dernier message apparaît (ou à l'ouverture d'un fil).
  useEffect(() => {
    const last = thread.length ? messageIdOf(thread[thread.length - 1]) : null;
    if (last && last !== lastIdRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: lastIdRef.current ? 'smooth' : 'auto' });
    }
    lastIdRef.current = last;
  }, [thread]);

  /* ---------- Rendu ---------- */
  const currentConv = useMemo(
    () => conversations.find((c) => c.memberId === selectedMemberId) || null,
    [conversations, selectedMemberId]
  );
  const totalMessages = currentConv ? Math.max(currentConv.messageCount, thread.length) : thread.length;

  // Séparateurs de jour + regroupement des messages consécutifs du même auteur
  const threadItems = useMemo(() => {
    const out = [];
    let lastDay = null;
    let prevAdmin = null;
    thread.forEach((m, i) => {
      const t = tsOf(m);
      const dk = t != null ? dayKey(t) : null;
      if (dk && dk !== lastDay) {
        out.push({ type: 'day', key: `day-${dk}`, label: dayLabel(t) });
        lastDay = dk;
        prevAdmin = null;
      }
      const admin = isFromAdmin(m);
      out.push({ type: 'msg', key: messageIdOf(m) || `i-${i}`, m, t, admin, grouped: prevAdmin === admin });
      prevAdmin = admin;
    });
    return out;
  }, [thread]);

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);
  const showThreadOnMobile = Boolean(selectedMemberId);

  return (
    <div className="space-y-4 fade-up h-[calc(100vh-8rem)] flex flex-col min-h-[480px]">
      <PageHeader title="Support" subtitle="Échanges avec les membres de la communauté" />

      {listError && (
        <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2 shrink-0">
          <span className="flex items-center gap-2">
            <ShieldAlert size={14} /> {listError}
          </span>
          <button type="button" className="underline" onClick={() => loadConversations()}>
            Réessayer
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[300px_1fr] gap-3">
        {/* Colonne gauche : liste (sur mobile, masquée quand une conversation est ouverte) */}
        <div
          className={`dsi-card flex-col min-h-0 overflow-hidden ${showThreadOnMobile ? 'hidden md:flex' : 'flex'}`}
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Headphones size={16} className="text-indigo-600" /> Conversations
              {conversations.length > 0 && (
                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
                  {conversations.length}
                </span>
              )}
              {totalUnread > 0 && (
                <span className="text-[10px] font-bold text-white bg-indigo-600 rounded-full px-2 py-0.5">
                  {totalUnread} non lu{totalUnread > 1 ? 's' : ''}
                </span>
              )}
            </p>
            <button
              type="button"
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              title="Actualiser"
              onClick={handleManualRefresh}
              disabled={refreshing || listLoading}
            >
              <RefreshCw size={14} className={refreshing || listLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {listLoading && !conversations.length ? (
              <p className="p-4 text-xs text-slate-500 text-center">Chargement…</p>
            ) : !conversations.length ? (
              <div className="p-4">
                <EmptyState title="Aucune conversation" message="Les échanges avec les membres apparaîtront ici." />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {conversations.map((c) => {
                  const unread = c.unreadCount > 0;
                  return (
                    <li key={c.memberId}>
                      <button
                        type="button"
                        onClick={() => selectConversation(c.memberId)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${
                          selectedMemberId === c.memberId ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                            <User size={16} className="text-indigo-600" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-slate-900 font-mono truncate">{c.memberId}</p>
                              <span
                                className={`text-[10px] shrink-0 ${unread ? 'text-indigo-600 font-bold' : 'text-slate-400'}`}
                              >
                                {formatListWhen(c.lastMessageAt)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <p
                                className={`text-[11px] truncate ${unread ? 'text-slate-800 font-semibold' : 'text-slate-500'}`}
                              >
                                {c.lastMessage.direction === 'support_to_member' ? 'Vous : ' : ''}
                                {c.lastMessage.body || '—'}
                              </p>
                              {unread && (
                                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                  {c.unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Zone centrale : fil de discussion (sur mobile, affichée seulement si une conversation est ouverte) */}
        <div
          className={`dsi-card flex-col min-h-0 overflow-hidden ${showThreadOnMobile ? 'flex' : 'hidden md:flex'}`}
        >
          {!selectedMemberId ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <EmptyState title="Sélectionnez une conversation" message="Choisissez un membre à gauche pour lire et répondre." />
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  className="md:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 text-slate-600"
                  title="Retour aux conversations"
                  onClick={closeConversation}
                >
                  <ArrowLeft size={18} />
                </button>
                <span className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                  <User size={16} className="text-indigo-600" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 font-mono truncate">{selectedMemberId}</p>
                  <p className="text-[10px] text-slate-500">
                    Conversation support · {totalMessages} message{totalMessages > 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50/60">
                {threadError && (
                  <p className="text-xs text-rose-700 font-semibold flex items-center gap-1 mb-3">
                    <ShieldAlert size={12} /> {threadError}
                    <button type="button" className="underline ml-1" onClick={() => loadThread(selectedMemberId)}>
                      Réessayer
                    </button>
                  </p>
                )}

                {hasMore && (
                  <div className="flex justify-center mb-3">
                    <button
                      type="button"
                      className="text-[11px] font-semibold text-indigo-700 bg-white border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50 disabled:opacity-60"
                      onClick={loadOlder}
                      disabled={loadingOlder}
                    >
                      {loadingOlder ? 'Chargement…' : 'Charger les messages précédents'}
                    </button>
                  </div>
                )}

                {threadLoading && !thread.length ? (
                  <p className="text-xs text-slate-500 text-center py-8">Chargement de la conversation…</p>
                ) : !thread.length && !threadError ? (
                  <p className="text-xs text-slate-500 text-center py-8">Aucun message dans cette conversation.</p>
                ) : (
                  threadItems.map((it) => {
                    if (it.type === 'day') {
                      return (
                        <div key={it.key} className="flex justify-center my-4 first:mt-0">
                          <span className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1">
                            {it.label}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={it.key}
                        className={`flex ${it.admin ? 'justify-end' : 'justify-start'} ${it.grouped ? 'mt-1' : 'mt-3'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                            it.admin
                              ? 'bg-indigo-600 text-white rounded-br-md'
                              : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{bodyOf(it.m) || '—'}</p>
                          <p
                            className={`text-[10px] mt-1 text-right ${it.admin ? 'text-indigo-200' : 'text-slate-400'}`}
                          >
                            {formatTime(it.t)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t border-slate-100 shrink-0 space-y-2">
                {sendError && (
                  <p className="text-xs text-rose-700 font-semibold flex items-center gap-1">
                    <ShieldAlert size={12} /> {sendError}
                  </p>
                )}
                <div className="flex gap-2 items-end">
                  <textarea
                    className="dsi-input text-sm flex-1 min-h-[44px] max-h-32 resize-y"
                    rows={2}
                    placeholder="Écrire une réponse…"
                    value={draft}
                    disabled={sending}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="dsi-btn-primary text-xs shrink-0 h-11 px-4"
                    disabled={sending || !draft.trim()}
                    onClick={handleSend}
                  >
                    <Send size={14} /> {sending ? 'Envoi…' : 'Envoyer'}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400">Entrée pour envoyer · Maj + Entrée pour un saut de ligne</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
